/**
 * Authentication Hooks
 *
 * Custom hooks for accessing and managing authentication state.
 */

import { useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { setAuthSignOut, setAuthTokenGetter } from '@/lib/axios'
import { firebaseAuthService } from '../api/authService'

/**
 * Hook to access authentication state and actions
 */
export const useAuth = () => {
  const store = useAuthStore()

  return {
    // State
    user: store.user,
    loading: store.loading,
    error: store.error,
    initialized: store.initialized,
    isAuthenticated: !!store.user,

    // Actions
    login: store.login,
    signup: store.signup,
    loginWithGoogle: store.loginWithGoogle,
    logout: store.logout,
    refreshUser: store.refreshUser,
    refreshPreferences: store.refreshPreferences,
    setError: store.setError,
  }
}

/**
 * Hook to initialize Firebase auth state listener
 * Should be called once at app startup
 */
export const useInitializeAuth = () => {
  const initializeAuthListener = useAuthStore((state) => state.initializeAuthListener)

  useEffect(() => {
    // Set auth token getter for axios interceptor.
    // `forceRefresh` 를 그대로 넘긴다 — 만료 응답을 받은 뒤 SDK 캐시를 건너뛰고 새 토큰을
    // 받아 와야 무음 갱신이 실제로 갱신이 된다.
    setAuthTokenGetter((forceRefresh) => firebaseAuthService.getIdToken(forceRefresh))

    // 되살릴 수 없는 401 을 받으면 인터셉터가 이걸 부른다. 스토어의 `logout` 이 아니라
    // 서비스를 직접 부르는 건, 아래 리스너가 `onAuthStateChanged` 로 `user` 를 비우는 게
    // 어차피 같은 결과이면서 헤더 스피너를 켰다 끄지 않아서다.
    setAuthSignOut(() => firebaseAuthService.logout())

    // Initialize auth state listener
    const unsubscribe = initializeAuthListener()

    return () => {
      unsubscribe()
    }
  }, [initializeAuthListener])
}

/**
 * Hook to require authentication
 * Throws error if user is not authenticated (useful for protected components)
 */
export const useRequireAuth = () => {
  const { user, loading, initialized } = useAuth()

  if (!initialized || loading) {
    return { user: null, loading: true }
  }

  if (!user) {
    throw new Error('Authentication required')
  }

  return { user, loading: false }
}
